import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { VendorProfile, useVendorQueries } from 'shared-lib';

interface VendorContextState {
  // Available vendor companies
  vendors: VendorProfile[];
  // Currently selected vendor
  currentVendor: VendorProfile | null;
  // Vendor loading state
  loading: boolean;
  // Set current vendor
  setCurrentVendor: (vendor: VendorProfile | null) => void;
  // Archive vendor (soft delete)
  archiveVendor: (vendorId: string) => Promise<void>;
  // Error state
  error: Error | null;
}

const VendorContext = createContext<VendorContextState | undefined>(undefined);

interface VendorContextProviderProps {
  children: ReactNode;
}

export const VendorContextProvider: React.FC<VendorContextProviderProps> = ({ children }) => {
  const [currentVendor, setCurrentVendor] = useState<VendorProfile | null>(null);
  const [error, setError] = useState<Error | null>(null);
  
  // Use the React Query hooks
  const vendorQueries = useVendorQueries();
  const { useVendorsByUser, useUpdateVendor } = vendorQueries;
  
  // Get vendor companies
  const { 
    data: vendors = [], 
    isLoading,
    refetch
  } = useVendorsByUser();
  
  // Filter out archived vendors
  const activeVendors = useMemo(() => {
    return vendors.filter(vendor => !vendor.isArchived);
  }, [vendors]);
  
  // Get update mutation
  const updateVendor = useUpdateVendor();
  
  // Archive vendor (soft delete)
  const archiveVendor = useCallback(async (vendorId: string) => {
    try {
      await updateVendor.mutateAsync({
        vendorId,
        data: { isArchived: true }
      });
      
      // If the archived vendor was the current one, select another one or null
      if (currentVendor?.id === vendorId) {
        const nextVendor = activeVendors.find(v => v.id !== vendorId) || null;
        setCurrentVendor(nextVendor);
      }
      
      // Refetch vendors
      refetch();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [updateVendor, currentVendor, activeVendors, refetch]);
  
  // When vendors load or user changes, set the default vendor
  useEffect(() => {
    if (!isLoading && activeVendors.length > 0 && !currentVendor) {
      setCurrentVendor(activeVendors[0]);
    }
    
    // If current vendor is not in the list anymore, reset it
    if (currentVendor && !activeVendors.find(v => v.id === currentVendor.id)) {
      setCurrentVendor(activeVendors[0] || null);
    }
  }, [isLoading, activeVendors, currentVendor]);
  
  const value = useMemo(() => {
    return {
      vendors: activeVendors,
      currentVendor,
      loading: isLoading,
      setCurrentVendor,
      archiveVendor,
      error,
    };
  }, [activeVendors, currentVendor, isLoading, archiveVendor, error]);
  
  return (
    <VendorContext.Provider value={value}>
      {children}
    </VendorContext.Provider>
  );
};

// Hook for using the vendor context
export const useVendorContext = (): VendorContextState => {
  const context = useContext(VendorContext);
  if (context === undefined) {
    throw new Error('useVendorContext must be used within a VendorContextProvider');
  }
  return context;
}; 